import axios, { AxiosError } from "axios";
import { API_BASE_URL } from "../config/env";

export type Bilingual = { en: string; sw: string };

/** A failed call, carrying the backend's bilingual message and machine code. */
export class ApiError extends Error {
  constructor(
    public messages: Bilingual,
    public status: number,
    public code: string | null,
    /** Reference to quote to support (server errors), also in the server log. */
    public ref: string | null = null
  ) {
    super(messages.en);
  }
}

const TOKEN_KEY = "safiri-agent-token";
const DEVICE_KEY = "safiri-agent-device";

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Session token; kept in memory too, so a blocked localStorage only costs a reload. */
let memoryToken: string | null = storage()?.getItem(TOKEN_KEY) ?? null;
export const tokenStore = {
  get: () => memoryToken,
  set: (t: string | null) => {
    memoryToken = t;
    try {
      if (t) storage()?.setItem(TOKEN_KEY, t);
      else storage()?.removeItem(TOKEN_KEY);
    } catch {
      // Not persisted.
    }
  },
};

/** A random id per browser: feeds the backend's duplicate-device check (spec §5.4). */
function deviceId(): string {
  try {
    const s = storage();
    let id = s?.getItem(DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      s?.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

export const http = axios.create({ baseURL: API_BASE_URL, timeout: 25000 });

http.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.set("Authorization", `Bearer ${t}`);
  const d = deviceId();
  if (d) config.headers.set("X-Device-Id", d);
  return config;
});

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

const T = (en: string, sw: string): Bilingual => ({ en, sw });

/**
 * Turn any failure into words the agent can act on: what went wrong, and
 * whether to retry, fix something or contact support (with a reference).
 */
function toApiError(err: unknown): ApiError {
  if (!axios.isAxiosError(err)) return new ApiError(T("Something went wrong in the app. Please reload the page and try again.", "Kuna hitilafu kwenye programu. Tafadhali pakia ukurasa upya ujaribu tena."), 0, null);
  const e = err as AxiosError<any>;
  const status = e.response?.status ?? 0;
  const body = e.response?.data;
  const m = body?.message;
  const code = typeof body?.error === "string" ? body.error : null;
  const ref: string | null = body?.meta?.requestId ?? null;
  const bilingual = m && typeof m === "object" && m.en ? (m as Bilingual) : null;

  if (status === 0) {
    if (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT")
      return new ApiError(T("The server took too long to answer. Check your connection and try again.", "Seva imechelewa kujibu. Hakiki mtandao wako ujaribu tena."), 0, "timeout");
    return new ApiError(T("No internet connection. Check your data or Wi-Fi and try again.", "Hakuna mtandao. Hakiki data au Wi-Fi yako ujaribu tena."), 0, "offline");
  }
  if (status === 429)
    return new ApiError(T("Too many attempts. Please wait 15 minutes and try again.", "Majaribio mengi mno. Tafadhali subiri dakika 15 ujaribu tena."), 429, "rate_limited");
  if (status === 502 || status === 503 || status === 504)
    return new ApiError(T("Safiri is briefly unavailable, usually during an update. Please try again in a minute.", "Safiri haipatikani kwa muda mfupi, mara nyingi wakati wa kusasisha. Tafadhali jaribu tena baada ya dakika moja."), status, "unavailable");
  if (bilingual) return new ApiError(bilingual, status, code, ref);
  if (status >= 500) {
    const tail = ref ? ` (ref ${ref})` : ` (error ${status})`;
    return new ApiError(T(`Something went wrong on our side. Please try again; if it keeps happening, contact Safiri support${tail}.`, `Kuna hitilafu upande wetu. Tafadhali jaribu tena; ikiendelea, wasiliana na huduma kwa wateja ya Safiri${tail}.`), status, code, ref);
  }
  if (status === 401) return new ApiError(T("Your session has ended. Please sign in again.", "Muda wako wa kuingia umeisha. Tafadhali ingia tena."), 401, code);
  if (status === 403) return new ApiError(T("Your account can't do this right now. Contact Safiri support if you think this is wrong.", "Akaunti yako haiwezi kufanya hili kwa sasa. Wasiliana na Safiri ikiwa unaona si sahihi."), 403, code);
  if (status === 404) return new ApiError(T("We couldn't find what you were looking for. It may have been removed.", "Hatukupata ulichokuwa ukitafuta. Huenda kimeondolewa."), 404, code);
  if (typeof m === "string" && m) return new ApiError(T(m, m), status, code, ref);
  return new ApiError(T(`That didn't work (error ${status}). Please try again or contact Safiri support.`, `Haikufanikiwa (hitilafu ${status}). Tafadhali jaribu tena au wasiliana na Safiri.`), status, code, ref);
}

async function run<T>(p: Promise<{ data: { data?: T } }>): Promise<T> {
  try {
    const res = await p;
    return res.data.data as T;
  } catch (err) {
    const e = toApiError(err);
    // Session gone (signed out elsewhere, password changed, expired).
    if (e.status === 401 && tokenStore.get()) onUnauthorized?.();
    throw e;
  }
}

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) => run<T>(http.get(url, { params })),
  post: <T>(url: string, body?: unknown) => run<T>(http.post(url, body ?? {})),
  patch: <T>(url: string, body?: unknown) => run<T>(http.patch(url, body ?? {})),
  del: <T>(url: string) => run<T>(http.delete(url)),
  upload: <T>(url: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return run<T>(http.post(url, form, { headers: { "Content-Type": "multipart/form-data" } }));
  },
};
